import * as React from "react";
import {
  logInWithEmailAndPassword,
  logInWithEmailLink,
} from "../../../utilities/Demo/auth";
import InputBarComponent from "../../InputBarComponent";
import { Snackbar } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import { CircularProgress } from "@mui/material";
import DialogTitle from "@mui/material/DialogTitle";
import Button from "@mui/material/Button";
import Box from "@mui/material/Box";
import Backdrop from "@mui/material/Backdrop";
import { useForm, Controller } from "react-hook-form";
import { styled } from "@mui/system";
import { useState } from "react";
import Alert from "@mui/material/Alert";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";

export default function LoginComponent({ open, setOpen, setFp, setLoading }) {
  const { t } = useTranslation();
  const { handleSubmit, control } = useForm();
  const [type, setType] = useState("");
  const [load, setLoad] = useState(false);
  const [msg, setMsg] = useState("");
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const signInType = queryParams.get("signInType");

  const onSubmit = async (data) => {
    setLoad(true);
    let loginSuccess = false;
    if (signInType === "emailLink") {
      loginSuccess = await logInWithEmailLink(data.email);
    } else {
      loginSuccess = await logInWithEmailAndPassword(data.email, data.password);
    }
    if (loginSuccess) {
      setMsg("Login Successful");
      setType("success");
      setLoading(true);
    } else {
      setMsg("Login Failed");
      setType("error");
    }
    setLoad(false);
  };

  return (
    <Box>
      <Snackbar
        open={msg !== ""}
        autoHideDuration={4000}
        anchorOrigin={{
          horizontal: "center",
          vertical: "top",
        }}
        onClose={() => {
          setMsg("");
        }}
      >
        <Alert 
          severity={type}
          sx={{
            borderRadius: 2,
            fontWeight: 600,
            fontSize: "0.95rem",
            boxShadow: "0 4px 12px rgba(0,0,0,0.12)",
            fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
            letterSpacing: "-0.01em",
          }}
        > 
          {msg} 
        </Alert>
      </Snackbar>
      {type !== "success" && (
        <Dialog
          PaperProps={{
            sx: {
              borderRadius: 3,
              maxWidth: "460px",
              width: "95vw",
              padding: 4,
              backgroundColor: "#ffffff",
              boxShadow: "0 8px 32px rgba(0,0,0,0.15)",
              position: "relative",
              overflow: "hidden",
              "&::before": {
                content: '""',
                position: "absolute",
                top: 0,
                left: 0,
                right: 0,
                height: "4px",
                background: "linear-gradient(90deg, #FF592C 0%, #FF7A52 100%)",
              },
            },
          }}
          scroll="body"
          BackdropComponent={styled(Backdrop, {
            name: "MuiModal",
            slot: "Backdrop",
            overridesResolver: (props, styles) => {
              return styles.backdrop;
            },
          })({
            zIndex: -1,
            backgroundColor: "rgba(0, 0, 0, 0.6)",
            backdropFilter: "blur(4px)",
          })}
          open={open}
          onClose={() => setOpen(false)}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: 2,
            }}
          >
            <Box
              sx={{
                width: 56,
                height: 56,
                borderRadius: "50%",
                backgroundColor: "rgba(255, 89, 44, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <LockOutlinedIcon sx={{ fontSize: 28, color: "#FF592C" }} />
            </Box>
          </Box>

          <DialogTitle
            sx={{
              fontSize: 28,
              fontWeight: 800,
              color: "#1a1a1a",
              paddingBottom: 0.5,
              paddingTop: 0,
              textAlign: "center",
              fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
              letterSpacing: "-0.03em",
              lineHeight: 1.2,
            }}
          >
            {t("welcomeView.login")}
            <IconButton
              onClick={() => setOpen(false)}
              size="small"
              sx={{
                position: "absolute",
                right: 16,
                top: 16,
                color: "#999",
                "&:hover": {
                  color: "#333",
                  backgroundColor: "rgba(0,0,0,0.05)",
                },
              }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </DialogTitle>

          <Box
            sx={{
              textAlign: "center",
              color: "#64748b",
              fontSize: "0.95rem",
              marginBottom: 3,
              paddingX: 2,
              fontWeight: 500,
              fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
              letterSpacing: "-0.01em",
              lineHeight: 1.5,
            }}
          >
            Enter your credentials to access your account
          </Box>

          <form onSubmit={handleSubmit(onSubmit)}>
            <DialogContent
              sx={{
                overflow: "hidden",
                paddingTop: 0,
                paddingBottom: 2,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              <Box sx={{ marginBottom: 2.5 }}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field: { onChange, value } }) => (
                    <InputBarComponent
                      name="email"
                      label={t("ProfileComponent.email")}
                      type="email"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Box>
              {signInType !== "emailLink" && (
                <Box sx={{ marginBottom: 1 }}>
                  <Controller
                    name="password"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                      <InputBarComponent
                        name="password"
                        label={t("welcomeView.password")}
                        type="password"
                        value={value}
                        onChange={onChange}
                      />
                    )}
                  />
                </Box>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                flexDirection: "column",
                gap: 1.5,
                paddingTop: 1,
                paddingBottom: 0,
                paddingLeft: 0,
                paddingRight: 0,
              }}
            >
              {load ? (
                <Box sx={{ width: "100%", display: "flex", justifyContent: "center", py: 2 }}>
                  <CircularProgress 
                    size={36}
                    thickness={4}
                    sx={{ color: "#FF592C" }} 
                  />
                </Box>
              ) : (
                <>
                  <Button
                    type="submit"
                    variant="contained"
                    fullWidth
                    sx={{
                      borderRadius: 2,
                      backgroundColor: "#FF592C",
                      padding: "13px 32px",
                      fontSize: "1.05rem",
                      fontWeight: 700,
                      textTransform: "none",
                      boxShadow: "0 4px 12px rgba(255, 89, 44, 0.3)",
                      fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
                      letterSpacing: "-0.01em",
                      "&:hover": {
                        backgroundColor: "#e64a1f",
                        boxShadow: "0 6px 16px rgba(255, 89, 44, 0.4)",
                        transform: "translateY(-1px)",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    {t("welcomeView.login")}
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    sx={{
                      padding: "10px 16px",
                      fontSize: "0.95rem",
                      fontWeight: 600,
                      textTransform: "none",
                      color: "#64748b",
                      fontFamily: "'Inter', 'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif",
                      letterSpacing: "-0.01em",
                      "&:hover": {
                        backgroundColor: "rgba(0,0,0,0.04)",
                        color: "#334155",
                      },
                    }}
                    onClick={() => {
                      setOpen(false);
                      setFp(true);
                    }}
                  >
                    {t("welcomeView.forgetPass")}
                  </Button>
                </>
              )}
            </DialogActions>
          </form>
        </Dialog>
      )}
    </Box>
  );
}
